import { CircleCheck, CircleX, Info, TriangleAlert } from "lucide-react";
import React, { ElementType, FC, ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";

import { IgIconButton, IgTypography } from "@intelligems/ig-theme";
import { Box, Grid } from "@mui/material";
import { AlertColor } from "@mui/material/Alert/Alert";
import { Theme } from "@mui/material/styles";
import { SxProps } from "@mui/system";
import { theme } from "@src/theme";

import { useGetOrg } from "../../api/organizations/get-organization";
import {
  dismissAlert,
  getIsAlertDismissed,
} from "../../store/slices/appSettings";

import { HiddenErrorBoundary } from "./IgErrorBoundary";

interface IgAlertProps {
  dismissId?: string;
  severity: AlertColor;
  loading?: boolean;
  loadingPercent?: number;
  title?: ReactNode | string;
  hideDismiss?: boolean;
  message?: ReactNode | string;
  longMessage?: ReactNode | string;
  buttons?: ReactNode[];
  sx?: SxProps<Theme>;
  variant?: "compact" | "regular";
  iconPosition?: "left" | "top";
  rawContent?: ReactNode;
}

const IconMap: Record<AlertColor, ElementType> = {
  error: CircleX,
  warning: TriangleAlert,
  success: CircleCheck,
  info: Info,
};

const ColorMap: Record<AlertColor, string> = {
  error: theme.igBorders.error.default,
  warning: theme.igBorders.warning.default,
  info: theme.igBorders.info.default,
  success: theme.igBorders.success.default,
};

const TitleColorMap: Record<AlertColor, string> = {
  error: theme.palette.text.tonal.error,
  warning: theme.palette.text.tonal.warning,
  info: theme.palette.text.tonal.info,
  success: theme.palette.text.tonal.success,
};

const BackgroundColorMap: Record<AlertColor, string> = {
  error: "white",
  warning: "white",
  info: "white",
  success: "white",
};

// Since this component needs access to redux we can't add to ig-theme

export const IgAlert: FC<IgAlertProps> = ({
  severity,
  dismissId,
  title,
  loadingPercent,
  loading,
  message,
  longMessage,
  buttons,
  sx,
  hideDismiss,
  iconPosition = "top",
  rawContent,
}) => {
  const Icon: ElementType = IconMap[severity];
  const dispatch = useDispatch();
  const org = useGetOrg();
  const isDismissed = useSelector(
    getIsAlertDismissed(org.data?.org.id, dismissId)
  );

  if (isDismissed) {
    return null;
  }

  return (
    <HiddenErrorBoundary>
      <Grid
        direction={"column"}
        container
        item
        sx={{
          minHeight: "64px",
          overflow: "hidden",
          border: `1px solid ${ColorMap[severity]}`,
          backgroundColor: BackgroundColorMap[severity],
          borderRadius: "5px",
          padding: "11px 8px 0px 20px",
          flexWrap: "nowrap",
          ...sx,
        }}
      >
        <Grid
          container
          item
          direction={"row"}
          justifyContent={"space-between"}
          sx={{
            minHeight: "40px",
            flexWrap: "nowrap",
          }}
        >
          <Grid
            direction={"column"}
            container
            item
            columnSpacing={0}
            sx={{ flexWrap: "nowrap", marginTop: "auto", marginBottom: "auto" }}
            flexGrow={8}
          >
            <Grid
              container
              item
              sx={{
                marginTop: "auto !important",
                marginBottom: "auto !important",
              }}
            >
              {iconPosition === "top" ? (
                <Grid
                  item
                  sx={{
                    marginRight: "10px",
                    marginTop: "4px",
                    marginBottom: "auto",
                  }}
                >
                  <Icon color={TitleColorMap[severity]} size={22} />
                </Grid>
              ) : null}
              <Grid
                item
                sx={{
                  marginRight: "5px",
                  marginTop: "auto !important",
                  marginBottom: "auto !important",
                }}
              >
                {title ? (
                  typeof title === "string" ? (
                    <IgTypography
                      sx={{
                        color: TitleColorMap[severity],
                        fontWeight: "600",
                      }}
                    >
                      {title}
                    </IgTypography>
                  ) : (
                    title
                  )
                ) : null}
              </Grid>
              {message ? (
                <Grid item sx={{ marginTop: "auto", marginBottom: "auto" }}>
                  {typeof message === "string" ? (
                    <IgTypography color={TitleColorMap[severity]}>
                      {message}
                    </IgTypography>
                  ) : (
                    <span>{message}</span>
                  )}
                </Grid>
              ) : null}
            </Grid>
            <Grid container item flexWrap={"nowrap"}>
              {iconPosition === "left" ? (
                <Grid
                  item
                  sx={{
                    marginRight: "10px",
                    marginTop: "auto",
                    marginBottom: "auto",
                  }}
                >
                  <Icon width={"22"} height={"22"} />
                </Grid>
              ) : null}
              {longMessage ? (
                <Grid item>
                  {typeof longMessage === "string" ? (
                    <IgTypography color={TitleColorMap[severity]}>
                      {longMessage}
                    </IgTypography>
                  ) : (
                    <span> {longMessage}</span>
                  )}
                </Grid>
              ) : null}
              {rawContent ? rawContent : null}
            </Grid>
          </Grid>
          {(dismissId && dispatch) || (buttons && buttons.length) ? (
            <Grid
              container
              item
              columnSpacing={1}
              alignItems={"center"}
              justifyContent={"flex-end"}
              sx={{
                maxWidth: "350px",
                flexWrap: "nowrap",
                marginTop: "auto",
                marginBottom: "auto",
              }}
            >
              {loadingPercent && loadingPercent > 0 ? (
                <IgTypography color={"placeholder"}>
                  {loadingPercent.toFixed(0)}%
                </IgTypography>
              ) : null}
              {(buttons || []).map((button, index) => {
                return (
                  <Grid item key={`alert-button-${index}`}>
                    {button}
                  </Grid>
                );
              })}
              {!hideDismiss && dismissId ? (
                <Grid
                  item
                  key={"dismiss-alert"}
                  sx={{ marginTop: "auto", marginBottom: "auto" }}
                >
                  <IgIconButton
                    iconType={"close"}
                    onClick={() =>
                      dispatch(
                        dismissAlert({
                          orgId: org.data.org.id,
                          alertId: dismissId,
                        })
                      )
                    }
                  />
                </Grid>
              ) : null}
            </Grid>
          ) : null}
        </Grid>
        <Grid
          item
          sx={{
            paddingTop: "8px",
            marginLeft: "-20px",
            marginRight: "-20px",
          }}
        >
          <Box
            className="indeterminate-progress-bar"
            sx={{
              height: "3px",
              opacity: loading ? 1 : 0,
              borderBottomLeftRadius: "15px",
              borderBottomRightRadius: "15px",
            }}
          >
            <Box
              sx={{
                backgroundColor: ColorMap[severity],
                height: "100%",
                width: `${loadingPercent}%`,
              }}
              className={
                loadingPercent === undefined
                  ? "indeterminate-progress-bar-value"
                  : undefined
              }
            />
          </Box>
        </Grid>
      </Grid>
    </HiddenErrorBoundary>
  );
};
